import { Box, Card, CardHeader, CardContent, Alert, LinearProgress, Grid, Button, Table, TableCell, TableHead, TableRow, TableBody, Typography } from "@mui/material";
import React, { useContext, useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import Chart from "react-google-charts";
import { BreadcrumbContext } from '../../../../components/Breadcrumb';
import { AuthContext } from "../../../../components/FirebaseAuth";
import { CloudFunctions } from "../../../../components/FirebaseAuth/firebase";
import Loader from "../../../../components/Loader";
import { Link } from "react-router-dom";

const cardSx = {
    backgroundColor: (theme) => theme.custom.card,
    boxShadow: (theme) => theme.custom.cardShadow,
    border: (theme) => `1px solid ${theme.custom.border}`,
};

const Overview = () => {
    const { t } = useTranslation();
    const mountedRef = useRef(true);

    const { userData } = useContext(AuthContext);
    const { setBreadcrumb } = useContext(BreadcrumbContext);

    const [loading, setLoading] = useState(true);
    const [loadingErrors, setLoadingErrors] = useState(true);

    const [error, setError] = useState("");

    const [usage, setUsage] = useState(0);
    const [requests, setRequests] = useState(0);
    const [dailyData, setDailyData] = useState([]);
    const [errorLog, setErrorLog] = useState([]);

    const [lastMonth, setLastMonth] = useState(false);

    function getDaysInMonth(lastMonth) {
        var date = new Date();
        var month = date.getUTCMonth();
        if(lastMonth){
            month = date.getUTCMonth()-1;
            date = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth()-1, 1));
        }else{
            month = date.getUTCMonth();
            date = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
        }
        var days = [];
        while (date.getUTCMonth() === month) {
          days.push(new Date(date));
          date.setUTCDate(date.getUTCDate() + 1);
        }
        return days;
    }

    useEffect(() => {
        setBreadcrumb([
            {
                to: "/",
                text: t('common.home'),
                active: false
            },
            {
                to: "/account/"+userData.currentAccount.id+"/",
                text: userData.currentAccount.name,
                active: false
            },
            {
                to: null,
                text: t('dashboards.title'),
                active: true
            }
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, setBreadcrumb, t]);

    useEffect(() => {
        // get month usage
        setLoading(true);

        const getMonthUsage = CloudFunctions.httpsCallable('getMonthUsage');
        getMonthUsage({
            accountId: userData.currentAccount.id,
            lastMonth: lastMonth
        }).then(res => {
            if (!mountedRef.current) return null
            let days = [];
            const allDays = getDaysInMonth(lastMonth);
            for(let i=0; i<allDays.length; i++){
                days[allDays[i].toISOString().split('T')[0]] = 0;
            }
            let total = 0;
            for(let i=0; i<res.data.length; i++){
                const row = res.data[i];
                const date = row.Hour.value.split("T")[0];
                if(days[date]){
                    days[date] = days[date] + row.Requests
                }else{
                    days[date] = row.Requests;
                }
                total = total + row.Requests;
            }
            setRequests(total);
            setUsage(total/userData.currentAccount.cap*100);
            let data = [];
            data.push(["Date", "Requests", {role: "style"}]);
            for(let k in days){
                data.push([k, days[k], "color: #1976d2"]);
            }
            setDailyData(data);
            setLoading(false);
        }).catch(err => {
            if (!mountedRef.current) return null
            setError(err.message);
            setLoading(false);
        });
    }, [userData, lastMonth])

    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    },[]);

    useEffect(() => {
        setLoadingErrors(true);
        const getErrorLog = CloudFunctions.httpsCallable('getErrorLog');
        getErrorLog({
            accountId: userData.currentAccount.id,
        }).then(res => {
            if (!mountedRef.current) return null
            setErrorLog(res.data);
            setLoadingErrors(false);

        }).catch(err => {
            if (!mountedRef.current) return null
            setLoadingErrors(false);
        });
    },[userData])

    return (
        <>
            <Card sx={cardSx}>
                <CardHeader title={t('dashboards.usageDashboard')} />
                <CardContent>
                    {(error) && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> }
                    {(loading) && <Loader text={t('common.loading')} size={16} />}
                    {!loading &&
                        <>
                            <Grid container>
                                <Grid item xs>
                                    {userData.currentAccount.cap === 0?(
                                        <Typography component="span">
                                            {t('dashboards.monthlyUsageUncapped', { requests: requests.toLocaleString() })}
                                            {!lastMonth && <i> {t('dashboards.updateFrequency')}</i>}
                                        </Typography>
                                    ):(
                                        <>
                                            <Typography>
                                                {t('dashboards.monthlyUsageCapped', {
                                                    requests: requests.toLocaleString(),
                                                    cap: userData.currentAccount.cap.toLocaleString(),
                                                    percent: usage.toFixed(2)
                                                })}
                                                {!lastMonth && <i> {t('dashboards.updateFrequency')}</i>}
                                            </Typography>
                                            {requests>0 && <LinearProgress variant="determinate" value={usage} sx={{borderRadius: '5px', height: '12px'}} />}
                                        </>
                                    )}
                                </Grid>
                                <Grid item>
                                    {lastMonth?(
                                        <Button onClick={() => setLastMonth(false)}>{t('dashboards.viewCurrentMonth')}</Button>
                                    ):(
                                        <Button onClick={() => setLastMonth(true)}>{t('dashboards.viewLastMonth')}</Button>
                                    )}
                                </Grid>
                            </Grid>

                            <Chart chartType="ColumnChart" width="100%" height="500px" data={dailyData} />
                        </>
                    }
                </CardContent>
            </Card>
            <Box sx={{ mt: 3 }}>
                <Card sx={cardSx}>
                    <CardHeader title={t('dashboards.errorDashboard')} />
                    <CardContent>
                        {(loadingErrors) && <Loader text={t('common.loading')} size={16} />}
                        {!loadingErrors &&
                            <>
                                <p>{t('dashboards.errorUpdateNotice')}</p>
                                {errorLog.length > 0?(
                                    <>
                                        <p>
                                            {t('dashboards.resolve403Prefix')}{' '}
                                            <Link to={'/account/'+userData.currentAccount.id+'/settings'}>{t('dashboards.resolve403Link')}</Link>
                                        </p>
                                        <p>
                                            {t('dashboards.resolve402Prefix')}{' '}
                                            <Link to={'/account/'+userData.currentAccount.id+'/billing/plan'}>{t('dashboards.resolve402Link')}</Link>
                                        </p>
                                        <Table aria-label="error log table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t('dashboards.colHour')}</TableCell>
                                                    <TableCell>{t('dashboards.colErrorType')}</TableCell>
                                                    <TableCell>{t('dashboards.colDomain')}</TableCell>
                                                    <TableCell>{t('dashboards.colErrors')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {errorLog.map((row, index) =>
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            {new Date(row.Hour.value).toLocaleDateString()}
                                                            &nbsp;
                                                            {new Date(row.Hour.value).toLocaleTimeString()}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.ResponseCode}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.Domain}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.Requests}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </>
                                ):
                                (
                                    <p>{t('dashboards.noErrors')}</p>
                                )}

                            </>
                        }
                    </CardContent>
                </Card>
            </Box>
        </>
    )
}

export default Overview;
